// src/pages/Audience.tsx
import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Eye, 
  TrendingUp, 
  Clock, 
  Download, 
  Calendar,
  Lightbulb,
  Film,
  Image,
  FileText,
  RefreshCw
} from 'lucide-react';
import { audienceApi } from '../services/audienceApi';
import { 
  EngagementDashboard, 
  DevicePerformance, 
  HourlyEngagement, 
  AudienceInsight,
  DailyEngagement
} from '../types/audience';
import { calculatePeriodComparison } from '../utils/audienceUtils';
import EngagementChart from '../components/audience/EngagementChart';
import HourlyHeatmap from '../components/audience/HourlyHeatmap';
import LoadingSpinner from '../components/LoadingSpinner';
import { Info } from 'lucide-react';

const Audience: React.FC = () => {
  const [dashboard, setDashboard] = useState<EngagementDashboard | null>(null);
  const [comparisonTrends, setComparisonTrends] = useState<DailyEngagement[]>([]);
  const [devicePerformance, setDevicePerformance] = useState<DevicePerformance[]>([]);
  const [hourlyData, setHourlyData] = useState<HourlyEngagement[]>([]);
  const [insights, setInsights] = useState<AudienceInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(7);
  const [insightsLoading, setInsightsLoading] = useState(false);

  const fetchAudienceData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        dashboardData,
        comparisonData,
        deviceData,
        hourlyEngagement
      ] = await Promise.all([
        // Current period - for topAssets, totalViewers, etc.
        audienceApi.getEngagementDashboard(selectedPeriod),
        // Double period - just for comparison calculations
        audienceApi.getEngagementDashboard(selectedPeriod * 2),
        audienceApi.getDevicePerformance(selectedPeriod),
        audienceApi.getHourlyEngagement(selectedPeriod)
        // Note: insights are loaded on-demand via refresh button
      ]);

      setDashboard(dashboardData);
      setComparisonTrends(comparisonData.dailyTrends);
      setDevicePerformance(deviceData);
      setHourlyData(hourlyEngagement);
      // Don't auto-load insights - user clicks refresh button
    } catch (err) {
      setError('Failed to fetch audience data');
      console.error('Error fetching audience data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudienceData();
  }, [selectedPeriod]);

  const handleExportReport = async () => {
    try {
      const blob = await audienceApi.exportEngagementReport(selectedPeriod, 'csv');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `audience-report-${selectedPeriod}days.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export report:', err);
    }
  };

  const refreshInsights = async () => {
    try {
      setInsightsLoading(true);
      const audienceInsights = await audienceApi.getAudienceInsights(selectedPeriod);
      setInsights(audienceInsights);
    } catch (err) {
      console.error('Failed to refresh insights:', err);
    } finally {
      setInsightsLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getAssetIcon = (assetName: string) => {
    const extension = assetName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'mp4':
      case 'avi':
      case 'mov':
        return <Film className="w-5 h-5 text-blue-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <Image className="w-5 h-5 text-green-500" />;
      default:
        return <FileText className="w-5 h-5 text-purple-500" />;
    }
  };

  const getAttentionScoreColor = (score: number): string => {
    if (score >= 0.8) return 'bg-green-100 text-green-800';
    if (score >= 0.6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getEngagementBar = (viewers: number, maxViewers: number) => {
    const percentage = Math.min((viewers / maxViewers) * 100, 100);
    return (
      <div className="w-16 h-4 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full"
          style={{ width: `${percentage}%` }}
        />
      </div>
    );
  };

  const getLocationText = (location: string | { locationName: string; city?: string; address?: string } | undefined): string => {
    if (!location) return '';
    if (typeof location === 'string') return location;
    return location.locationName || 'Unknown Location';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
        <button 
          onClick={fetchAudienceData}
          className="mt-2 text-red-800 hover:text-red-900 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No audience data available</p>
      </div>
    );
  }

  // Calculate period comparisons from the 2x period data
  const facesComparison = calculatePeriodComparison(comparisonTrends, 'totalViewers');
  const engagementComparison = calculatePeriodComparison(comparisonTrends, 'totalEngagementSeconds');

  // Use current period dashboard data for display
  const peakFaces = dashboard.dailyTrends.length > 0 
    ? Math.max(...dashboard.dailyTrends.map(d => d.totalViewers)) 
    : 0;
  const averageFaceDuration = dashboard.totalViewers > 0 
    ? dashboard.totalEngagementSeconds / dashboard.totalViewers 
    : 0;

  const maxViewers = Math.max(...devicePerformance.map(d => d.totalViewers), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Audience Analytics</h1>
          <p className="text-gray-600">Track viewer engagement across your digital signage network</p>
        </div>
        <div className="flex items-center space-x-3">
          <select 
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button 
            onClick={handleExportReport}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Faces</p>
              <p className="text-3xl font-bold text-gray-900">{dashboard.totalViewers.toLocaleString()}</p>
              <p className={`text-sm ${facesComparison.colorClass}`}>{facesComparison.displayText}</p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg. Attention Score</p>
              <p className="text-3xl font-bold text-gray-900">
                {dashboard.topAssets.length > 0 ? 
                  ((dashboard.topAssets.reduce((sum, asset) => sum + Math.min(Number(asset.averageAttentionScore), 1), 0) / 
                     Math.max(dashboard.topAssets.length, 1)) * 100).toFixed(1) + '%'
                  : '0.0%'
                }
              </p>
              {dashboard.topAssets.length > 0 ? (
                <p className="text-sm text-gray-500">Based on {dashboard.topAssets.length} assets</p>
              ) : (
                <p className="text-sm text-gray-500">No data yet</p>
              )}
            </div>
            <Eye className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Peak Faces</p>
              <p className="text-3xl font-bold text-gray-900">{peakFaces.toLocaleString()}</p>
              {peakFaces > 0 ? (
                <p className="text-sm text-gray-500">In a single day</p>
              ) : (
                <p className="text-sm text-gray-500">No data yet</p>
              )}
            </div>
            <TrendingUp className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg. Face Duration</p>
              <p className="text-3xl font-bold text-gray-900">{formatDuration(Math.round(averageFaceDuration))}</p>
              <p className={`text-sm ${engagementComparison.colorClass}`}>{engagementComparison.displayText}</p>
            </div>
            <Clock className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Engagement Trend Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Engagement Trend ({selectedPeriod} Days)</h3>
          <EngagementChart data={dashboard.dailyTrends} />
        </div>

        {/* Device Performance */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Performing Devices</h3>
          <div className="space-y-4">
            {devicePerformance.slice(0, 4).map((device, index) => (
              <div key={device.deviceId} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className={`w-3 h-3 rounded-full mr-3 ${
                      index === 0 ? 'bg-blue-500' : 
                      index === 1 ? 'bg-green-500' : 
                      index === 2 ? 'bg-purple-500' : 'bg-orange-500'
                    }`} 
                  />
                  <div>
                    <span className="text-sm font-medium">{device.deviceName}</span>
                    {device.location && (
                      <p className="text-xs text-gray-500">
                        {getLocationText(device.location)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right flex items-center space-x-3">
                  <div>
                    <span className="text-sm font-medium text-gray-900">
                      {device.totalViewers.toLocaleString()} faces
                    </span>
                  </div>
                  {getEngagementBar(device.totalViewers, maxViewers)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content Performance Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Content Performance</h3>
          <p className="text-sm text-gray-600">How your assets are performing with viewers</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Asset
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Faces
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Attention Score
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dashboard.topAssets.slice(0, 5).map((asset) => (
                <tr key={asset.assetId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getAssetIcon(asset.assetName)}
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">{asset.assetName}</div>
                        <div className="text-sm text-gray-500">Asset ID: {asset.assetId}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {asset.totalViewers.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      getAttentionScoreColor(Math.min(Number(asset.averageAttentionScore), 1))
                    }`}>
                      {(Math.min(Number(asset.averageAttentionScore), 1) * 100).toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hourly Engagement Heatmap */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Hourly Engagement Heatmap</h3>
          <p className="text-sm text-gray-600">Peak viewing times across your network</p>
        </div>
        <div className="p-6">
          <HourlyHeatmap data={hourlyData} />
        </div>
      </div>

      {/* AI Insights */}
      <div className="bg-gradient-to-r from-blue-50 to-gray-50 rounded-lg p-6 border border-blue-200">
        <div className="flex items-start justify-between">
          <div className="flex items-start flex-1">
            <div className="flex-shrink-0">
              <Lightbulb className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-medium text-blue-900 mb-2">AI-Powered Insights</h3>
              {insightsLoading ? (
                <div className="flex items-center space-x-2 text-blue-600">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Generating insights...</span>
                </div>
              ) : insights.length > 0 ? (
                <div className="space-y-2">
                  {insights.map((insight, index) => (
                    <p key={index} className="text-sm text-blue-800">
                      <strong>{insight.title}:</strong> {insight.description}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-blue-600">Click refresh to generate AI-powered insights from your data.</p>
              )}
            </div>
          </div>
          <button
            onClick={refreshInsights}
            disabled={insightsLoading}
            className="ml-4 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh insights"
          >
            <RefreshCw className={`w-5 h-5 ${insightsLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Audience;
